import { ViewStateEditablePathCollapsingRule } from './types';
import { CollapsePathsCommands } from './collapsePathsCommands';
import { Header3, Paragraph } from '@ardoq/typography';
import {
  ButtonGroup,
  SmallSecondaryButton,
  SmallGhostButton,
} from '@ardoq/button';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { PathCollapsingNodeSelector } from './PathCollapsingNodeSelector';
import { NumberInput, TextInput } from '@ardoq/forms';
import { ErrorNotification } from '@ardoq/status-ui';
import { t, type I18nLocale } from '@ardoq/i18n';
import { translateCollapsingRuleError } from './CollapsingRuleErrorMessage';

type EditPathCollapsingRuleProps = {
  rule: ViewStateEditablePathCollapsingRule;
  commands: CollapsePathsCommands;
  i18nLocale: I18nLocale;
};

const EditPathCollapsingRule = ({
  rule,
  commands,
  i18nLocale,
}: EditPathCollapsingRuleProps) => {
  return (
    <Box borderWidth={1} borderRadius="r8" padding="medium">
      <Stack gap="medium">
        <Header3>
          {t(
            i18nLocale,
            'viewpointBuilder:collapsePaths.edit.header',
            'Select a path to collapse'
          )}
        </Header3>
        <Paragraph color="textSubtle">
          {t(
            i18nLocale,
            'viewpointBuilder:collapsePaths.edit.description',
            'Select 2 component types in the dataset metamodel. All the references and component types in between them will be hidden in your dataset.'
          )}
        </Paragraph>
        <PathCollapsingNodeSelector
          label={t(
            i18nLocale,
            'viewpointBuilder:collapsePaths.edit.from',
            'From'
          )}
          selectedNode={rule.startNode}
          i18nLocale={i18nLocale}
        />
        <PathCollapsingNodeSelector
          label={t(i18nLocale, 'viewpointBuilder:collapsePaths.edit.to', 'To')}
          selectedNode={rule.endNode}
          isDisabled={!rule.startNode}
          i18nLocale={i18nLocale}
        />
        {rule.keepExpandedLevels !== null && (
          <NumberInput
            label={t(
              i18nLocale,
              'viewpointBuilder:collapsePaths.edit.keepExpandedLevels',
              'Expanded descendant levels'
            )}
            value={rule.keepExpandedLevels}
            onValueChange={commands.keepExpandedLevelsUpdated}
            allowBlank={false}
            helperText={t(
              i18nLocale,
              'viewpointBuilder:collapsePaths.edit.keepExpandedLevelsHelper',
              "Choose how many levels of the context component's descendants stay visible. Everything below that level is collapsed. 0 collapses all descendants."
            )}
          />
        )}
        <FlexBox justify="center">
          <SmallGhostButton onClick={() => commands.clearCollapsedPathNodes()}>
            {t(
              i18nLocale,
              'viewpointBuilder:collapsePaths.edit.clearSelection',
              'Clear selection'
            )}
          </SmallGhostButton>
        </FlexBox>
        {rule.errorMessage ? (
          <ErrorNotification>
            {translateCollapsingRuleError(rule.errorMessage, i18nLocale)}
          </ErrorNotification>
        ) : (
          <Box
            backgroundColor="surfaceDefaultSubtle"
            borderRadius="r4"
            padding="large"
          >
            <TextInput
              label={t(
                i18nLocale,
                'viewpointBuilder:collapsePaths.edit.displayText',
                'Path display text'
              )}
              value={rule.displayText}
              onValueChange={commands.displayTextUpdated}
              helperText={t(
                i18nLocale,
                'viewpointBuilder:collapsePaths.edit.displayTextHelper',
                'This will be displayed where the hidden data is. It is not a part of your metamodel and can be edited later.'
              )}
            />
          </Box>
        )}
        <ButtonGroup justify="end">
          <SmallGhostButton
            onClick={() => commands.cancelEditPathCollapsingRule(rule)}
          >
            {t(
              i18nLocale,
              'viewpointBuilder:collapsePaths.edit.cancel',
              'Cancel'
            )}
          </SmallGhostButton>
          <SmallSecondaryButton
            onClick={() => commands.savePathCollapsingRule(rule)}
            isDisabled={!rule.isSavable}
          >
            {t(i18nLocale, 'viewpointBuilder:collapsePaths.edit.save', 'Save')}
          </SmallSecondaryButton>
        </ButtonGroup>
      </Stack>
    </Box>
  );
};

export { EditPathCollapsingRule };
